import { ListItemButton, ListItemIcon } from '@mui/material';
import AcceptIcon from '@mui/icons-material/TaskTwoTone';
import { gridRowSelectionIdsSelector, useGridApiContext, useGridSelector } from '@mui/x-data-grid-pro';
import { useAcceptLoadTenderMutation } from '@api/Trinium/Endpoints/EDI/slice';
import { useMemo } from 'react';
import useCurrentTerminal from '@hooks/useCurrentTerminal';
import { useAppDispatch } from '@reduxStore';
import { queueAnnouncement } from '@/Infrastructure/Components/Announcements/AnnouncementSlice';
import { getApiErrorMessage } from '@api/getApiError';
import { queueModal } from '@components/Modals/modalSlice';
import DisabledTooltip from '@components/DisabledTooltip';
import { setEdiLoading, clearEdiLoading } from '../../ediLoadTenderSlice';

export default function AcceptLoadTenderListItem() {
	const { number: terminalNumber } = useCurrentTerminal();
	const gridApi = useGridApiContext();
	const selectedRowIds = useGridSelector(gridApi, gridRowSelectionIdsSelector);
	const appDispatch = useAppDispatch();

	const [acceptEdis] = useAcceptLoadTenderMutation();

	const isDisabled = useMemo(() => {
		if (selectedRowIds.size < 1) return 'This action requires at least one row to be selected.';

		return null;
	}, [selectedRowIds]);

	const handleBtnClick = () => {
		const selectedIds: number[] = [];
		for (const [key] of selectedRowIds) {
			selectedIds.push(key as number);
		}

		if (selectedIds.length > 1) {
			appDispatch(
				queueModal({
					Component: 'confirmPopup',
					Props: {
						prompt: "You've selected multiple EDI load tenders. Are you sure you want to accept all?",
						confirmedAction: () => {
							appDispatch(setEdiLoading(selectedIds));
							void acceptEdis({
								terminalNumber: terminalNumber,
								ids: selectedIds
							})
								.unwrap()
								.catch((ex: unknown) => {
									appDispatch(
										queueAnnouncement({
											severity: 'error',
											title: getApiErrorMessage(ex),
											clearable: true
										})
									);
								})
								.finally(() => {
									appDispatch(clearEdiLoading(selectedIds));
								});
						}
					}
				})
			);
		} else {
			appDispatch(setEdiLoading(selectedIds));
			void acceptEdis({
				terminalNumber: terminalNumber,
				ids: selectedIds
			})
				.unwrap()
				.catch((ex: unknown) => {
					appDispatch(
						queueAnnouncement({
							severity: 'error',
							title: getApiErrorMessage(ex),
							clearable: true
						})
					);
				})
				.finally(() => {
					appDispatch(clearEdiLoading(selectedIds));
				});
		}
	};

	return (
		<DisabledTooltip
			message={isDisabled}
			placement='right'
		>
			<ListItemButton
				disabled={Boolean(isDisabled)}
				onClick={handleBtnClick}
			>
				<ListItemIcon sx={{ minWidth: '24px' }}>
					<AcceptIcon
						fontSize='small'
						color='success'
					/>
				</ListItemIcon>
				Accept
			</ListItemButton>
		</DisabledTooltip>
	);
}
